/**
 * Poly-vs-Kalshi cross-venue divergence — the data layer for the trader-card
 * divergence warning. Dan's ask (voice-note, BigTB Ops): "If Polymarket vs
 * Kalshi pricing differs by >= 2 cents, trigger an alert/warning." Pure +
 * venue-agnostic: given a Kalshi book and its paired Polymarket book (paired
 * upstream by marketStore.getGames via canonicalGameKey), it returns the
 * signed mid-to-mid gap and a freshness verdict.
 *
 * Freshness is load-bearing. The same request explicitly calls out that "the
 * displayed [Polymarket] percentage needs to be accurate enough for alerting."
 * A frozen poly feed keeps rendering its last mid with no visible "dead" marker
 * (see "Stale prices mask dead hydra feeds" in CLAUDE.md), so an alert built on
 * the mid alone would fire off a stale gap. We gate on poly orderbook recency
 * (marketStore.getOrderbookAgeMs) — the mid is derived from the bid/ask ladder,
 * so the orderbook's age is the honest "is this poly price real right now"
 * signal. A stale book yields a !fresh entry that never alerts.
 *
 * Lane note: Polymarket *market data* reaches both g1 lanes over the hydra WS —
 * the Polymarket geoblock is order-POST-only (it gates the relay's outbound CLOB
 * client, not the feed). So this warning is live on g1.pm staging too whenever
 * hydra's poly feed is up; it's UI/sound-only (no order placement), so it's
 * harmless there and can be validated on either lane.
 */
import type { VenueBook } from '@/views/ValueDashboard/types';

/** The gap (in cents) at/above which the warning arms. Dan's ask: >= 2¢. */
export const DIVERGENCE_THRESHOLD_CENTS = 2;
/** Hysteresis floor. Once armed, the warning only re-arms after the gap falls
 *  back below this — so a value chattering around 2.0¢ doesn't re-ring. */
export const DIVERGENCE_REARM_CENTS = 1.5;
/** Poly orderbook older than this is treated as stale → no warning. Generous
 *  enough that a merely-quiet (but live) poly book isn't suppressed; tight
 *  enough that a frozen or geoblocked feed is. */
export const POLY_STALE_MS = 120_000;

export interface DivergenceInfo {
  /** Absolute mid-to-mid gap in cents (>= 0). */
  divergenceCents: number;
  /** polyMid - kalshiMid. Positive = Polymarket is pricing the side higher. */
  signedCents: number;
  kalshiMid: number;
  polyMid: number;
  /** The paired Polymarket market ticker (freshness lookup + debug). */
  polyTicker: string;
  /** Age of the poly orderbook in ms, or null if none ever received. */
  polyAgeMs: number | null;
  /** True when the poly book is recent enough to trust for alerting. */
  fresh: boolean;
}

function bestBid(book: VenueBook | undefined | null): number | null {
  if (!book?.yes?.length) return null;
  return Math.max(...book.yes.map((l) => l.priceCents));
}

function bestAsk(book: VenueBook | undefined | null): number | null {
  if (!book?.no?.length) return null;
  return 100 - Math.max(...book.no.map((l) => l.priceCents));
}

/** Mid = (best bid + best ask) / 2, mirroring StreamRift's PriceMatrix. Null
 *  when either side of the ladder is empty (no tradeable two-sided book). */
export function bookMid(book: VenueBook | undefined | null): number | null {
  const bid = bestBid(book);
  const ask = bestAsk(book);
  if (bid === null || ask === null) return null;
  return (bid + ask) / 2;
}

/**
 * Compute the cross-venue divergence for one paired side. Returns null when
 * either venue lacks a two-sided book (nothing to compare). `polyAgeMs` is the
 * poly orderbook age from marketStore.getOrderbookAgeMs(polyTicker).
 */
export function computeDivergence(
  kalshiBook: VenueBook | undefined | null,
  polyBook: VenueBook | undefined | null,
  polyTicker: string,
  polyAgeMs: number | null,
): DivergenceInfo | null {
  const kalshiMid = bookMid(kalshiBook);
  const polyMid = bookMid(polyBook);
  if (kalshiMid === null || polyMid === null) return null;
  const signedCents = polyMid - kalshiMid;
  return {
    divergenceCents: Math.abs(signedCents),
    signedCents,
    kalshiMid,
    polyMid,
    polyTicker,
    polyAgeMs,
    fresh: polyAgeMs !== null && polyAgeMs <= POLY_STALE_MS,
  };
}

/** True when the gap is real enough to surface: armed threshold AND fresh poly.
 *  A stale poly book never counts — an unreliable price must not raise a flag. */
export function isDivergenceActive(
  info: DivergenceInfo | null | undefined,
): info is DivergenceInfo {
  return !!info && info.fresh && info.divergenceCents >= DIVERGENCE_THRESHOLD_CENTS;
}

export interface LatchResult {
  /** The next armed state per ticker (carries the latch). */
  nextArmed: Map<string, boolean>;
  /** Tickers that crossed into the alert band this tick (ring + flash these). */
  fired: string[];
}

/**
 * Pure per-tick step of the divergence alert latch. Edge-triggered with
 * hysteresis + freshness — extracted so the subtle rules are unit-tested rather
 * than buried in a component effect. Given the previous armed state, the current
 * ticker→divergence map, and the set of still-held tickers, returns the next
 * armed state + which tickers should ring/flash.
 *
 * Rules:
 *  - first sight of a ticker adopts its current active state SILENTLY (no ring),
 *    so a market already diverged on page load doesn't ring on arrival;
 *  - ring once on an inactive→active transition (false→true);
 *  - re-arm (true→false) only on a FRESH gap below DIVERGENCE_REARM_CENTS — a
 *    stale reading can neither ring (active requires fresh) nor disarm, so a
 *    frozen mid drifting under the floor can't reset the latch;
 *  - a held ticker momentarily absent from `byTicker` (its book went one-sided,
 *    so computeDivergence returned null) KEEPS its latch — hysteresis survives
 *    feed noise; a ticker no longer held is dropped and re-seeds on re-entry.
 */
export function stepDivergenceLatch(
  prevArmed: Map<string, boolean>,
  byTicker: Map<string, DivergenceInfo>,
  held: ReadonlySet<string>,
): LatchResult {
  const nextArmed = new Map<string, boolean>();
  const fired: string[] = [];

  // Carry latches for held tickers absent from this tick's map (transient
  // one-sided/null book) so a momentary drop-out doesn't re-seed hysteresis.
  for (const [ticker, was] of prevArmed) {
    if (held.has(ticker) && !byTicker.has(ticker)) nextArmed.set(ticker, was);
  }

  for (const [ticker, info] of byTicker) {
    if (!held.has(ticker)) continue; // not held → no latch (drop)
    // Read fields before the type-guard call — isDivergenceActive narrows
    // `info`, and the rearm branch would otherwise see `info` as `never`.
    const fresh = info.fresh;
    const cents = info.divergenceCents;
    const active = isDivergenceActive(info);
    if (!prevArmed.has(ticker)) {
      nextArmed.set(ticker, active); // first sight: silent seed
      continue;
    }
    const was = prevArmed.get(ticker)!;
    if (active && !was) {
      nextArmed.set(ticker, true);
      fired.push(ticker);
    } else if (!active && was && fresh && cents < DIVERGENCE_REARM_CENTS) {
      nextArmed.set(ticker, false);
    } else {
      nextArmed.set(ticker, was);
    }
  }

  return { nextArmed, fired };
}

function fmtMag(cents: number): string {
  const rounded = Math.round(cents * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** Short badge label, e.g. "Poly +2.5¢" / "Poly −2¢" (signed, poly-relative). */
export function fmtDivergenceBadge(info: DivergenceInfo): string {
  const sign = info.signedCents > 0 ? '+' : info.signedCents < 0 ? '−' : '';
  return `Poly ${sign}${fmtMag(info.divergenceCents)}¢`;
}

/** Full tooltip copy — states the gap, both mids, and the no-execution caveat. */
export function divergenceTooltip(info: DivergenceInfo): string {
  const dir =
    info.signedCents > 0 ? 'higher than' : info.signedCents < 0 ? 'lower than' : 'level with';
  return (
    `Polymarket is pricing this market ${fmtMag(info.divergenceCents)}¢ ${dir} Kalshi ` +
    `(Poly ${info.polyMid.toFixed(1)}¢ / Kalshi ${info.kalshiMid.toFixed(1)}¢). ` +
    `Signal only — no Polymarket execution from here.`
  );
}
